import { trackEvent } from '@repo/common/analytics';
import type { INotification } from '../../../../api/types/models/notification';
import { ANALYTICS_EVENTS } from '../../constants';
import type {
	EnhancedNotification,
	INotificationTaskActionsProps,
} from '../../types';
import { TEAM_ACCESS_REQUEST_CREATED_EVENT } from '../../utils/teamAccessRequestUtils';
import { AccessRequestAction } from './AccessRequestAction';
import { ChangeRequestAction } from './ChangeRequestAction';
import { DataAccessRequestActions } from './DataAccessRequestActions';
import { MonitorIncidentActions } from './MonitorIncidentActions';
import { TeamAccessRequestAction } from './TeamAccessRequestAction';

function isEnhancedNotification(
	notification: INotification | EnhancedNotification
): notification is EnhancedNotification {
	return 'computedMetadata' in notification;
}

export function TaskActions({
	selected,
	applyChangeRequest,
	applyAccessRequest,
	incident,
	onUpdateIncident,
	changeRequestApplied,
	accessRequestApplied,
	user,
	workspace,
	approveTeamAccessRequest,
	rejectTeamAccessRequest,
	isApprovingTeamAccessRequest,
	isRejectingTeamAccessRequest,
}: INotificationTaskActionsProps) {
	if (
		isEnhancedNotification(selected) &&
		selected.computedMetadata?.isMonitorIncident
	) {
		return (
			<MonitorIncidentActions
				incident={incident}
				onUpdateIncident={async (status) => {
					trackEvent(
						ANALYTICS_EVENTS.INCIDENT_STATUS_UPDATE,
						{
							notificationId: selected.id,
							incidentId: incident?.id || 'unknown',
							newStatus: status,
							previousStatus: incident?.status || 'unknown',
						},
						user,
						workspace
					);
					await onUpdateIncident(status);
				}}
			/>
		);
	}

	if (selected.event === 'change_request') {
		return (
			<ChangeRequestAction
				notification={selected}
				changeRequestApplied={changeRequestApplied}
				applyChangeRequest={(data) => {
					trackEvent(
						ANALYTICS_EVENTS.CHANGE_REQUEST_APPLY,
						{
							notificationId: selected.id,
							requestId: selected.request?.id || 'unknown',
							resourceType: selected.resource_type || 'unknown',
						},
						user,
						workspace
					);
					applyChangeRequest(data);
				}}
			/>
		);
	}

	if (selected.event === 'access_request') {
		return (
			<AccessRequestAction
				notification={selected}
				accessRequestApplied={accessRequestApplied}
				applyAccessRequest={(data) => {
					trackEvent(
						ANALYTICS_EVENTS.ACCESS_REQUEST_APPLY,
						{
							notificationId: selected.id,
							requestId: selected.request?.id || 'unknown',
							resourceType: selected.resource_type || 'unknown',
						},
						user,
						workspace
					);
					applyAccessRequest(data);
				}}
			/>
		);
	}

	if (
		isEnhancedNotification(selected) &&
		selected.event === TEAM_ACCESS_REQUEST_CREATED_EVENT
	) {
		return (
			<TeamAccessRequestAction
				notification={selected}
				approveTeamAccessRequest={approveTeamAccessRequest}
				rejectTeamAccessRequest={rejectTeamAccessRequest}
				isApprovingTeamAccessRequest={isApprovingTeamAccessRequest}
				isRejectingTeamAccessRequest={isRejectingTeamAccessRequest}
			/>
		);
	}

	if (
		isEnhancedNotification(selected) &&
		selected.computedMetadata?.isDataAccessRequest
	) {
		return <DataAccessRequestActions notification={selected} />;
	}

	return null;
}
